import { API_BASE_URL } from '@/utils/env';
import { getFreshToken } from '@/utils/auth';
import { auth } from '@/firebase';
import { BRAND } from '@/brand';

/* ───────────────────────────────────────────────────────────────────────────
   Who the signed-in admin is, and which admin brought a client in.

   Attribution rides the referral pipeline that already exists: a signup link
   carrying `?ref=<uid>` is stashed as localStorage `signup_ref`, posted to
   /auth/signup as `referredBy`, and persisted on the user record through email
   verification. An admin's own uid works as that ref value because admins are
   real Firebase Auth users, so an admin invite link needs no new backend field
   — it is a referral whose referrer happens to be staff.

   Which is why attribution is resolved by LOOKUP rather than by tagging the
   link: `referredBy` is just a uid, and whether it belongs to an admin is
   answered by /auth/admins at display time.
   ─────────────────────────────────────────────────────────────────────────── */

export type AdminRef = { uid: string; name: string; email: string };

/** The registration form each brand sends people to. */
const SIGNUP_PATH = BRAND === 'corporate' ? '/accounting/signup' : '/consulting/signup';

/**
 * The invite link for one admin. Built from the CURRENT origin, not the brand's
 * canonical domain, so a link copied on staging stays on staging.
 */
export const inviteLinkFor = (uid: string): string =>
  `${window.location.origin}${SIGNUP_PATH}?ref=${encodeURIComponent(uid)}`;

function readAdminBlob(): Record<string, any> {
  try { return JSON.parse(localStorage.getItem('adminUser') || '{}') || {}; } catch { return {}; }
}

function writeAdminBlob(patch: Record<string, any>) {
  try {
    localStorage.setItem('adminUser', JSON.stringify({ ...readAdminBlob(), ...patch }));
  } catch { /* quota — the uid is re-resolvable, so this is only a cache */ }
}

// ── admin directory (uid → admin), cached for the session ──────────────────

let dirCache: Record<string, AdminRef> | null = null;
let dirInflight: Promise<Record<string, AdminRef>> | null = null;

async function fetchDirectory(): Promise<Record<string, AdminRef>> {
  const token = await getFreshToken();
  try {
    const res = await fetch(`${API_BASE_URL}/auth/admins`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) return dirCache || {};
    const json = await res.json();
    const list: any[] = Array.isArray(json?.admins) ? json.admins : [];
    const map: Record<string, AdminRef> = {};
    for (const a of list) {
      if (!a?.uid) continue;
      map[a.uid] = {
        uid: a.uid,
        name: (a.name || a.displayName || a.email || 'Admin').trim(),
        email: a.email || '',
      };
    }
    // Never cache an empty directory — a transient failure would otherwise make
    // every admin-sourced client look like an ordinary client for the session.
    if (Object.keys(map).length) dirCache = map;
    return dirCache || {};
  } catch {
    return dirCache || {};
  }
}

/** uid → admin, for labelling who brought a client in. Cached per session. */
export function getAdminDirectory(): Promise<Record<string, AdminRef>> {
  if (dirCache) return Promise.resolve(dirCache);
  if (!dirInflight) dirInflight = fetchDirectory().finally(() => { dirInflight = null; });
  return dirInflight;
}

/** Forget the cached directory (after adding or removing a team member). */
export const clearAdminDirectoryCache = () => { dirCache = null; };

// ── the signed-in admin's own uid ──────────────────────────────────────────

/**
 * The current admin's uid, or null if it can't be established.
 *
 * Three sources, cheapest first: the login blob, the live Firebase session, then
 * the admin directory matched on the stored email. The third exists because
 * admins who signed in before the login pages started recording the uid still
 * have a blob without one, and re-authenticating to get an invite link would be
 * a poor trade.
 */
export async function getAdminUid(): Promise<string | null> {
  const blob = readAdminBlob();
  if (typeof blob.uid === 'string' && blob.uid) return blob.uid;

  const live = auth.currentUser?.uid;
  if (live) { writeAdminBlob({ uid: live }); return live; }

  const email = typeof blob.email === 'string' ? blob.email.toLowerCase() : '';
  if (!email) return null;
  const dir = await getAdminDirectory();
  const hit = Object.values(dir).find(a => a.email.toLowerCase() === email);
  if (hit) { writeAdminBlob({ uid: hit.uid }); return hit.uid; }
  return null;
}
